// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import InputRange, { Range } from 'react-input-range';
import Autosuggest from 'react-autosuggest';

import Select2, { DataSelect2 } from 'wdna-select2';

import { UserDO } from 'wdna-components';

import '../../../../../../assets/css/input-range.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';

/**
 * Function to format the sensor label
 *
 * @param value value of the label to format
 * @returns the formatted value
 */
function formatLabel(value: number): string {
  const formatvalue: string = value
    .toFixed(2)
    .toString()
    .replace('.', ':')
    .replace(':5', ':3')
    .replace('24', '0');

  return formatvalue;
}

/**
 * Function to get latitude coordinate by default
 *
 * @returns default latitude value
 */
function latitudeDefault(): number | undefined {
  const host: string = window.location.hostname;
  let latitude: number | undefined;
  if (host === 'test.sensorpatrol.com') latitude = 39.634722047264056;
  return latitude;
}

/**
 * Function to get longitude coordinate by default
 *
 * @returns default longitude value
 */
function longitudeDefault(): number | undefined {
  const host: string = window.location.hostname;
  let longitude: number | undefined;
  if (host === 'test.sensorpatrol.com') longitude = 2.634530736359011;
  return longitude;
}

interface Props {
  duplicate: boolean;
  formValues: any;
  inputProps1: any;
  inputProps2: any;
  suggestions: any[];
  showMap: () => void;
  loadType: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onChangeStandard: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onChangeRange: (l: string, v: Range | number) => void;
  onChangeUpdate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeLocation: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  renderSuggestion: (suggestion: any, params: any) => React.ReactNode;
  getSuggestionValue: (suggestion: any) => string;
  onSuggestionsClearRequested: () => void;
  onSuggestionsFetchRequested: (r: any) => void;
}

/**
 * Function to generate a form component
 *
 * @param props properties of the component
 * @returns jsx element with the form
 */
export default function Form(props: Props): JSX.Element {
  const { companiesArray, firmwareArray, typeSensorArray, locationArray, statusArray, timetable } = props.formValues;
  const { isNew, data } = props.formValues;
  latitudeDefault();

  const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
  const userIsAdmin: boolean = user.type === 'adm';
  const userIsSuper: boolean = user.superuser === 1;
  const day: Date = new Date();
  const today: string = moment(day).format('YYYY-MM-DD');

  return (
    <div>
      <fieldset>
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Code</b> &nbsp;
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="text"
              value={data.code || ''}
              name="code"
              onChange={props.onChangeStandard}
              required
              readOnly={(!isNew && !props.duplicate) && !userIsSuper}
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-6">
            <label className="label">
              <b>Type</b> &nbsp; &nbsp;&nbsp;
            </label>
            <Select2
              name="typeSensor.id"
              data={typeSensorArray.map((group: any): DataSelect2 => {
                return {
                  text: `${group.typeModuleName}: [${group.code}] - ${group.name}`,
                  id: group.id,
                };
              })}
              value={(data.typeSensor && data.typeSensor.id) || ''}
              options={{
                placeholder: 'Select option',
              }}
              onChange={props.onChange}
              style={{ width: '80%' }}
              disabled={!isNew}
            />
            {!isNew && (
              <button
                type="button"
                onClick={props.loadType}
                className="btn btn-secondary"
                style={{ margin: '0px 0 0 5px', padding: '0 12px', height: '32px' }}
              >
                Edit
              </button>
            )}
            <label>&nbsp;&nbsp;*</label>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Name</b> &nbsp;&nbsp;
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="text"
              value={data.name || ''}
              name="name"
              onChange={props.onChangeStandard}
              readOnly
            />
          </div>
          <div className="col-6">
            <label className="label">
              <b>Description</b> &nbsp;&nbsp;
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="text"
              value={data.description || ''}
              name="description"
              onChange={props.onChangeStandard}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Company</b> &nbsp;
            </label>
            <Select2
              name="company.id"
              data={companiesArray
                .filter((comp: any) => {
                  return userIsAdmin || comp.name === user?.extra?.company;
                })
                .map((group: any): DataSelect2 => {
                  return { text: group.name, id: group.id };
                })}
              value={(data.company && data.company.id) || ''}
              options={{
                placeholder: 'Select option',
              }}
              onChange={props.onChange}
              style={{ width: '80%' }}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-6">
            <label className="label">
              <b>Status</b> &nbsp;
            </label>
            <Select2
              name="status"
              data={statusArray.map((item: any): DataSelect2 => {
                return { id: item.id, text: item.name };
              })}
              value={data.status || ''}
              options={{
                placeholder: 'Select option',
              }}
              onChange={props.onChangeStandard}
              style={{ width: '80%' }}
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-3">
            <label className="label">
              <b>Date Install</b>
            </label>
            <input
              id="dateInstall"
              type="date"
              name="dateInstall"
              className="inputNew wdna-fit-date-size"
              value={moment(data.dateInstall || day).format('YYYY-MM-DD')}
              onChange={props.onChangeStandard}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-3">
            <label className="label">
              <b>Date Online</b>
            </label>
            <input
              id="dateOnline"
              type="date"
              name="dateOnline"
              className="inputNew wdna-fit-date-size"
              value={moment(data.dateOnline || day).format('YYYY-MM-DD')}
              onChange={props.onChangeStandard}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-3">
            <label className="label">
              <b>Date Offline</b>
            </label>
            <input
              id="dateOffline"
              type="date"
              name="dateOffline"
              className="inputNew wdna-fit-date-size"
              value={data.dateOffline ? moment(data.dateOffline).format('YYYY-MM-DD') : undefined}
              onChange={props.onChangeStandard}
            />
            &nbsp;&nbsp;
          </div>
          <div className="col-3">
            <label className="label">
              <b>Date Action</b>
            </label>
            <input
              id="dateAction"
              type="date"
              name="dateAction"
              className="inputNew wdna-fit-date-size"
              value={data.dateAction ? moment(data.dateAction).format('YYYY-MM-DD') : undefined}
              onChange={props.onChangeStandard}
            />
            &nbsp;&nbsp;
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Firmware</b> &nbsp;
            </label>
            <Select2
              name="firmware.id"
              data={firmwareArray.map((item: any): DataSelect2 => {
                return { id: item.id, text: item.ver };
              })}
              value={(data.firmware && data.firmware.id) || ''}
              options={{
                placeholder: 'Select option',
              }}
              onChange={props.onChange}
              style={{ width: '65%' }}
              disabled={!data.update}
            />
            {data.update && (
              <label>&nbsp;&nbsp;*</label>
            )}
            &emsp;&emsp;
            <input
              className="inputNew"
              type="checkbox"
              name="update"
              data-handler-type="boolean"
              checked={data.update || false}
              onChange={props.onChangeUpdate}
            />
            &nbsp;&nbsp;
            <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              Update
            </label>
          </div>
          <div className="col-6">
            <label className="label">
              <b>Command 1</b>&nbsp; &nbsp;
            </label>
            <Autosuggest
              suggestions={props.suggestions}
              onSuggestionsFetchRequested={props.onSuggestionsFetchRequested}
              onSuggestionsClearRequested={props.onSuggestionsClearRequested}
              getSuggestionValue={props.getSuggestionValue}
              renderSuggestion={props.renderSuggestion}
              inputProps={props.inputProps1}
              highlightFirstSuggestion
            />
            &emsp;&emsp;
            <input
              className="inputNew"
              type="checkbox"
              checked={data.persistent1 || false}
              id="persistent1"
              name="persistent1"
              data-handler-type="boolean"
              onChange={props.onChange}
            />
            &nbsp;&nbsp;
            <label htmlFor="persistent1" className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              Persistent
            </label>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-2">
            <label className="label">
              <b>Time NC:</b>
            </label>
            <input
              className="inputNew"
              style={{ width: '150px' }}
              type="number"
              value={data.timeNc || 60}
              name="timeNc"
              onChange={props.onChangeStandard}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-4">
            <label className="label">
              <b>Scheduling NC:</b>
            </label>
            <InputRange
              maxValue={24}
              minValue={0}
              step={0.5}
              name="timetable"
              value={timetable}
              allowSameValues
              formatLabel={formatLabel}
              onChange={(value: Range | number): void => props.onChangeRange('timetable', value)}
            />
          </div>
          <div className="col-6">
            <label className="label">
              <b>Command 2</b> &nbsp;
            </label>
            <Autosuggest
              suggestions={props.suggestions}
              onSuggestionsFetchRequested={props.onSuggestionsFetchRequested}
              onSuggestionsClearRequested={props.onSuggestionsClearRequested}
              getSuggestionValue={props.getSuggestionValue}
              renderSuggestion={props.renderSuggestion}
              inputProps={props.inputProps2}
              highlightFirstSuggestion
            />
            &emsp;&emsp;
            <input
              className="inputNew"
              type="checkbox"
              checked={data.persistent2 || false}
              id="persistent2"
              name="persistent2"
              data-handler-type="boolean"
              onChange={props.onChange}
            />
            &nbsp;&nbsp;
            <label htmlFor="persistent2" className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              Persistent
            </label>
          </div>
        </div>
      </fieldset>
      <header>
        <h2>
          <span className="widget-icon" style={{ padding: '4px' }}>
            <i className="fa fa-edit" />
          </span>
          Location &nbsp;&nbsp;
        </h2>
      </header>
      <fieldset className="form">
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Location</b> &nbsp; &nbsp;&nbsp;
            </label>
            <Select2
              name="location.id"
              id="location.id"
              data={locationArray.map((group: any): DataSelect2 => {
                return { text: `${group.name} (${group.address})`, id: group.id };
              })}
              value={data.location?.id || ''}
              options={{
                placeholder: 'Select option',
              }}
              onChange={props.onChangeLocation}
              style={{ width: '80%' }}
            />
          </div>
          <div className="col-2">
            <label className="label">
              <b>Latitude:</b>
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="number"
              value={data.latitude || latitudeDefault()}
              name="latitude"
              step="any"
              onChange={props.onChangeStandard}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-2">
            <label className="label">
              <b>Longitude:</b>
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="number"
              value={data.longitude || longitudeDefault()}
              name="longitude"
              step="any"
              onChange={props.onChangeStandard}
              required
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
          <div className="col-2">
            <label className="label">&nbsp;</label>
            <button
              type="button"
              onClick={props.showMap}
              className="btn btn-secondary"
              style={{ margin: '0px 0 0 5px', padding: '0 22px', height: '31px' }}
            >
              Map
            </button>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Comments</b> &nbsp;&nbsp;
            </label>
            <label className="input">
              <textarea
                rows={3}
                className="inputNew"
                style={{ width: '80%' }}
                name="comments"
                value={data.comments || ''}
                onChange={props.onChangeStandard}
              />
            </label>
          </div>
          <div className="col-3">
            <label className="label">
              <b>Sim Code</b> &nbsp;&nbsp;
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="text"
              value={data.sim || ''}
              name="sim"
              onChange={props.onChangeStandard}
            />
          </div>
          <div className="col-3">
            <label className="label">
              <b>Router Code</b> &nbsp;&nbsp;
            </label>
            <input
              className="inputNew"
              style={{ width: '80%' }}
              type="text"
              value={data.router || ''}
              name="router"
              onChange={props.onChangeStandard}
            />
          </div>
        </div>
        <br />
        <div hidden>
          <div className="row">
            <div className="col-6">
              <label className="label">Last Date &nbsp;</label>
              <input
                className="inputNew"
                type="datetime"
                value={data.dateLast || ''}
                name="dateLast"
                step="5"
                onChange={props.onChange}
                readOnly
              />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
